/* eslint-disable ember/no-incorrect-calls-with-inline-anonymous-functions */
/* eslint-disable ember/no-runloop */
import { scheduleOnce } from '@ember/runloop';
import Service, { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import intercomBoot, {
  onHide,
  onShow,
  onUnreadCountChange,
  showNewMessage as intercomShowNewMessage,
  startChecklist as intercomStartChecklist,
  startTour as intercomStartTour,
  update as intercomUpdate,
} from '@intercom/messenger-js-sdk';
import type { IntercomSettings } from '@intercom/messenger-js-sdk/types';
import type { User } from 'frontend-utils/models';
import { PRODUCT_NAMES } from 'frontend-utils/utils/constants/constant-values';
import { DOMAIN_NAME } from 'frontend-utils/utils/constants/domain-utils';
import { isTripadvisorDomain } from 'frontend-utils/utils/domain-checks';
import { THEME, type Theme } from 'frontend-utils/utils/prism/theme';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import Cookies from 'js-cookie';
import ENV from 'lumen/config/environment';
import { ROUTE_DICT } from 'lumen/utils/constants/route-constants';

const rateInsightRoutes = Object.entries(ROUTE_DICT)
  .filter(([, productNames]) =>
    productNames.find(
      (productName) => productName === PRODUCT_NAMES.PRODUCT_RATE_INSIGHT,
    ),
  )
  .map(([routeName]) => routeName);

const INTERCOM_THEME = {
  DARK: 'dark',
  LIGHT: 'light',
  SYSTEM: 'system',
};

const themeToIntercomTheme = {
  [THEME.DARK]: INTERCOM_THEME.DARK,
  [THEME.LIGHT]: INTERCOM_THEME.LIGHT,
  [THEME.SYSTEM]: INTERCOM_THEME.SYSTEM,
  [THEME.HIGH_CONTRAST_DARK]: INTERCOM_THEME.DARK,
  [THEME.HIGH_CONTRAST_LIGHT]: INTERCOM_THEME.LIGHT,
  [THEME.HIGH_CONTRAST_SYSTEM]: INTERCOM_THEME.SYSTEM,
} as const;

export default class IntercomService extends Service {
  @service declare access: Services['access'];
  @service declare destination: Services['destination'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare theme: Services['theme'];

  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked booted = false;
  @tracked hideMessenger = false;
  @tracked intercomHelpArticleLink = '';
  @tracked intercomSessionId?: string;

  tripadvisorHelpRedirect = `https://tripadvisor.${DOMAIN_NAME}/process-redirect/`;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get enabledAndBooted() {
    return ENV.intercom.isEnabled && this.booted;
  }

  private boot(user: User): void {
    let intercomSettings: IntercomSettings = {
      api_base: `https://${ENV.intercom.appId}.intercom-messenger.com`,
      app_id: ENV.intercom.appId,
      created_at: user.createdOn.unix(),
      language: user.locale,
      language_override: user.locale,
      name: `${user.firstName} ${user.lastName}`,
      hide_default_launcher: this.hideMessenger,
      theme_mode: themeToIntercomTheme[this.theme.currentTheme],
    };

    // intercomHash is undefined in tests. This prevents Intercom from crashing.
    if (user.intercomHash) {
      if (!isTripadvisorDomain()) {
        intercomSettings.email = user.email;
      }
      intercomSettings.user_hash = user.intercomHash;
      // @ts-expect-error: Type 'string | null' is not assignable to type 'string | undefined'.
      intercomSettings.user_id = user.id;
    }

    if (user.intercomCustomAttributes) {
      intercomSettings = {
        ...intercomSettings,
        ...user.intercomCustomAttributes,
      };
    }

    try {
      let unreadCount = 0;
      intercomBoot(intercomSettings);
      onUnreadCountChange((count: number) => {
        if (count < unreadCount) {
          this.usageAnalytics.track('Intercom Message Read', {
            eventTrigger: 'intercom-button',
          });
        }
        unreadCount = count;
      });
      onShow(() =>
        this.usageAnalytics.track('Intercom Interaction Started', {
          eventTrigger: 'intercom-button',
        }),
      );
      onHide(() =>
        this.usageAnalytics.track('Intercom Interaction Ended', {
          eventTrigger: 'intercom-button',
        }),
      );
      this.booted = true;
    } catch (err) {
      this.errorTracking.captureException(err);
    }
  }

  redirectToIntercomHelp(articleLink = ''): void {
    if (this.hotelAndMarketService.hotelOrMarket?.isTripadvisorRebranding) {
      // We set the session id every time since we can't get it in the constructor (Intercom has not booted yet)
      // We also can't do it in the boot (tried it, but the cookie was not always immediately set)
      // Seemed safest to do it here
      this.intercomSessionId = Cookies.get(
        `intercom-session-${ENV.intercom.appId}`,
      );
      this.intercomHelpArticleLink = articleLink;

      scheduleOnce('afterRender', this, () => {
        // @ts-expect-error: We know that the form exists
        (document.forms['intercom-form'] as HTMLFormElement).submit();
        this.intercomHelpArticleLink = '';
      });
    } else {
      openUrl(`https://help.${DOMAIN_NAME}/${articleLink}`);
    }
  }

  showNewMessage(message?: string): void {
    if (!ENV.intercom.isEnabled) {
      return;
    }

    // @ts-expect-error: Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
    intercomShowNewMessage(message);
  }

  startChecklist(checklistId: string): void {
    if (!ENV.intercom.isEnabled) {
      return;
    }

    intercomStartChecklist(checklistId);
  }

  startTour(tourId: string): void {
    if (!ENV.intercom.isEnabled) {
      return;
    }

    // Note: Right now the product tour is only visible for the MI page.
    // If we ever introduce this on more pages, we'll need to introduce a mapping of pages to Intercom tour ids

    // Note 2: There is no real feedback after this request has been done. If somethings wrong with the tour/Intercom has troubles/... nothing will happen :(
    // This is why there is no error handling here right now.
    intercomStartTour(tourId);
  }

  update(routeName: string | undefined): void {
    if (!ENV.intercom.isEnabled) {
      return;
    }

    const isUserLoggedIn = Boolean(this.hotelAndMarketService.user);
    if (!isUserLoggedIn) {
      return;
    }

    if (!routeName) {
      return;
    }

    if (this.access.hasFeature('settings.disable_intercom')) {
      const isRateInsightRoute = Boolean(
        rateInsightRoutes.find((route) => routeName.startsWith(route)),
      );
      const isEventsRoute = routeName.startsWith('hotel.events');
      const isOverviewRoute = routeName.startsWith('hotel.overview');
      const isSettingsRoute = routeName.startsWith('hotel.settings');
      const hideIntercomForRoute =
        isRateInsightRoute ||
        isEventsRoute ||
        isOverviewRoute ||
        isSettingsRoute;

      this.hideMessenger = hideIntercomForRoute;
    }

    if (!this.access.hasFeature('settings.disable_intercom')) {
      this.hideMessenger = false;
    }

    if (!this.booted) {
      this.boot(this.hotelAndMarketService.user);
    }

    intercomUpdate({
      hide_default_launcher: this.hideMessenger,
    });
  }

  updateDestination(): void {
    if (!this.enabledAndBooted) {
      return;
    }

    if (this.access.isStaffUser) {
      return;
    }

    const companyId = `destination_${this.destination?.destinationSubscription?.id}`;

    intercomUpdate({
      company: {
        id: companyId,
      },
      hide_default_launcher: this.hideMessenger,
      has_theme_access: this.theme.hasPermissions,
    });
  }

  updateHotel(): void {
    if (!this.enabledAndBooted) {
      return;
    }

    if (this.access.isStaffUser) {
      return;
    }

    const companyId = `hotel_${this.hotelAndMarketService.hotel?.id}`;

    intercomUpdate({
      company: {
        id: companyId,
      },
      last_viewed_hotel_id: this.hotelAndMarketService.hotel?.id,
      hide_default_launcher: this.hideMessenger,
      has_theme_access: this.theme.hasPermissions,
    });
  }

  updateMarket(): void {
    if (!this.enabledAndBooted) {
      return;
    }

    if (this.access.isStaffUser || this.hotelAndMarketService.isMyHotels) {
      return;
    }

    const companyId = `market_${this.hotelAndMarketService.market?.id}`;

    intercomUpdate({
      company: {
        id: companyId,
      },
      hide_default_launcher: this.hideMessenger,
      has_theme_access: this.theme.hasPermissions,
    });
  }

  updateTheme(theme: Theme): void {
    if (!this.enabledAndBooted) {
      return;
    }

    intercomUpdate({
      theme_mode: themeToIntercomTheme[theme],
    });
  }
}

declare module '@ember/service' {
  interface Registry {
    intercom: IntercomService;
  }
}
